import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useStreams } from '../app/StreamsProvider.jsx'
import { NotificationTypes, useNotifications } from '../hooks/useNotifications.jsx'
import useSessionStorageState from '../hooks/useSessionStorageState.js'
import { normalizeError } from '../utils/errors.js'
import { FIELD_SCHEMA } from '../activity-stream/inspector-schema.js'
import {
  CONTROL_PANEL_MIN_WIDTH,
  CONTROL_PANEL_MAX_WIDTH,
  DEFAULT_CONTROL_PANEL_WIDTH
} from '../activity-stream/controlConstants.js'
import {
  createStatement as createStatementRequest,
  deleteStatement as deleteStatementRequest,
  listStatements as listStatementsRequest,
  validateStatement as validateStatementRequest
} from '../services/statementsService.js'
import StatementForm from './StatementForm.jsx'
import StatementList from './StatementList.jsx'
import StatementOutput from './StatementOutput.jsx'
import '../activity-stream/styles.css'
import '../styles/statements.css'

const STORAGE_KEYS = {
  instance: 'webui-active-instance',
  statementsCollapsed: 'statements-panel-collapsed',
  outputCollapsed: 'statements-output-collapsed',
  controlPanelWidth: 'statements-control-width',
  outputPanelWidth: 'statements-output-width'
}

const sortStatements = (statements) => {
  return [...statements].sort((a, b) => {
    const instanceCompare = (a.instanceName ?? '').localeCompare(b.instanceName ?? '')
    if (instanceCompare !== 0) {
      return instanceCompare
    }
    return (a.statementName ?? '').localeCompare(b.statementName ?? '')
  })
}

const MIN_EDITOR_WIDTH = 460
const MAX_EDITOR_WIDTH = 1080

const StatementsPanel = () => {
  const { notify } = useNotifications()
  const [selectedInstance, setSelectedInstance] = useSessionStorageState(STORAGE_KEYS.instance, '')
  const [statements, setStatements] = useState([])
  const [listStatus, setListStatus] = useState('idle')
  const [listError, setListError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [validationFeedback, setValidationFeedback] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [filters, setFilters] = useState({ search: '', instance: '' })
  const [activeStatement, setActiveStatement] = useState(null)

  const [isStatementsCollapsed, setIsStatementsCollapsed] = useSessionStorageState(
    STORAGE_KEYS.statementsCollapsed,
    false
  )
  const [isOutputCollapsed, setIsOutputCollapsed] = useSessionStorageState(
    STORAGE_KEYS.outputCollapsed,
    false
  )

  const [controlPanelWidth, setControlPanelWidth] = useSessionStorageState(
    STORAGE_KEYS.controlPanelWidth,
    DEFAULT_CONTROL_PANEL_WIDTH
  )
  const [isResizingControlPanel, setIsResizingControlPanel] = useState(false)
  const controlResizeSnapshotRef = useRef({ startX: 0, startWidth: DEFAULT_CONTROL_PANEL_WIDTH })

  const [outputPanelWidth, setOutputPanelWidth] = useSessionStorageState(
    STORAGE_KEYS.outputPanelWidth,
    420
  )
  const [isResizingOutputPanel, setIsResizingOutputPanel] = useState(false)
  const outputResizeSnapshotRef = useRef({ startX: 0, startWidth: 420 })

  const { statementsStream } = useStreams()
  const {
    events: streamEvents,
    eventSamples,
    validationLog,
    recordValidationResult,
    setInstance: setStreamInstance,
    setStatement: setStreamStatement
  } = statementsStream

  const availableInstances = useMemo(() => {
    const set = new Set()
    statements.forEach((statement) => {
      if (statement.instanceName) {
        set.add(statement.instanceName)
      }
    })
    if (selectedInstance) {
      set.add(selectedInstance)
    }
    return Array.from(set).sort()
  }, [statements, selectedInstance])

  const filteredStatements = useMemo(() => {
    return statements.filter((statement) => {
      if (filters.instance && statement.instanceName !== filters.instance) {
        return false
      }
      if (filters.search) {
        const lower = filters.search.toLowerCase()
        return (
          statement.statementName?.toLowerCase().includes(lower) ||
          statement.statement?.toLowerCase().includes(lower)
        )
      }
      return true
    })
  }, [statements, filters])

  const layoutStyle = useMemo(() => {
    if (isStatementsCollapsed) {
      return {}
    }
    return { '--as-control-panel-width': `${Math.round(controlPanelWidth)}px` }
  }, [controlPanelWidth, isStatementsCollapsed])

  const loadStatements = async () => {
    setListStatus((previous) => (previous === 'loaded' ? 'refreshing' : 'loading'))
    setListError(null)
    try {
      const response = await listStatementsRequest()
      const allStatements = sortStatements(response?.statements ?? [])
      setStatements(allStatements)
      setListStatus('loaded')
      if (!selectedInstance && allStatements.length > 0) {
        setSelectedInstance(allStatements[0].instanceName ?? '')
      }
    } catch (error) {
      const normalized = normalizeError(error, 'Failed to load statements')
      setListError(normalized)
      setListStatus('error')
      notify({
        type: NotificationTypes.ERROR,
        title: 'Failed to load statements',
        message: normalized.message,
        autoDismissMs: 8_000
      })
    }
  }

  useEffect(() => {
    loadStatements()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if ((filters.instance ?? '') === (selectedInstance ?? '')) {
      return
    }
    setFilters((previous) => ({ ...previous, instance: selectedInstance ?? '' }))
  }, [filters.instance, selectedInstance])

  const handleCreateStatement = async (payload) => {
    setIsSubmitting(true)
    setValidationFeedback(null)
    try {
      const created = await createStatementRequest(payload)
      setStatements((previous) => sortStatements([created, ...previous]))
      setListStatus('loaded')
      notify({
        type: NotificationTypes.SUCCESS,
        title: 'Esper statement published',
        message: `${created.statementName} registered for ${created.instanceName}.`,
        autoDismissMs: 5_000
      })
      if (created.instanceName) {
        setSelectedInstance(created.instanceName)
      }
      setActiveStatement(created)
      return created
    } catch (error) {
      const normalized = normalizeError(error, 'Unable to create statement')
      if (normalized.status === 409) {
        notify({
          type: NotificationTypes.WARNING,
          title: 'Esper rejected duplicate',
          message: normalized.message || 'A statement with this name already exists in this instance.',
          autoDismissMs: 7_000
        })
      } else {
        notify({
          type: NotificationTypes.ERROR,
          title: 'Esper statement creation failed',
          message: normalized.message,
          autoDismissMs: 7_000
        })
      }
      throw new Error(normalized.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleValidateStatement = async ({ instanceName, statement }) => {
    if (!statement || !statement.trim()) {
      setValidationFeedback({ status: 'error', message: 'Provide statement text before validating.' })
      return null
    }
    setIsValidating(true)
    try {
      const response = await validateStatementRequest({
        instanceName: instanceName || undefined,
        statement
      })

      // Check the 'valid' field in response to determine success/failure
      const isValid = response?.valid ?? false
      const message = response?.message ?? (isValid ? 'Statement validated successfully.' : 'Validation failed')

      // Pass full validation response
      setValidationFeedback({
        status: isValid ? 'success' : 'error',
        message,
        valid: isValid,
        errors: response?.errors ?? [],
        warnings: response?.warnings ?? [],
        validationType: response?.validationType
      })

      return response
    } catch (error) {
      const normalized = normalizeError(error, 'Validation failed')
      // Include full error details from backend response
      setValidationFeedback({
        status: 'error',
        message: normalized.message,
        valid: false,
        errors: normalized.data?.errors ?? [],
        warnings: normalized.data?.warnings ?? [],
        validationType: normalized.data?.validationType
      })
      return null
    } finally {
      setIsValidating(false)
    }
  }

  const handleDeleteStatement = async (statementId) => {
    if (!statementId) {
      return
    }
    setDeletingId(statementId)
    try {
      await deleteStatementRequest(statementId)
      setStatements((previous) => previous.filter((statement) => statement.id !== statementId))
      notify({
        type: NotificationTypes.SUCCESS,
        title: 'Esper statement deleted',
        message: 'Statement removed successfully.',
        autoDismissMs: 4_000
      })
    } catch (error) {
      const normalized = normalizeError(error, 'Failed to delete statement')
      notify({
        type: NotificationTypes.ERROR,
        title: 'Esper statement delete failed',
        message: normalized.message,
        autoDismissMs: 7_000
      })
    } finally {
      setDeletingId(null)
    }
  }

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters)
    if (nextFilters.instance !== undefined) {
      setSelectedInstance(nextFilters.instance)
    }
  }

  const handleStatementSelect = (statement) => {
    setActiveStatement(statement)
    if (statement?.instanceName) {
      setSelectedInstance(statement.instanceName)
    }
    setStreamStatement(statement?.statementName ?? '')
    setValidationFeedback(null)
  }

  const handleCancelEdit = () => {
    setActiveStatement(null)
    setValidationFeedback(null)
  }

  const stopControlPanelResize = useCallback(() => {
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [])

  const handleControlPanelResizeStart = useCallback(
    (event) => {
      if (isStatementsCollapsed) {
        return
      }
      event.preventDefault()
      controlResizeSnapshotRef.current = {
        startX: event.clientX,
        startWidth: controlPanelWidth
      }
      setIsResizingControlPanel(true)
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'
    },
    [controlPanelWidth, isStatementsCollapsed]
  )

  useEffect(() => {
    if (!isResizingControlPanel) {
      return
    }

    const handleMouseMove = (event) => {
      const delta = event.clientX - controlResizeSnapshotRef.current.startX
      const proposedWidth = controlResizeSnapshotRef.current.startWidth + delta
      const constrainedWidth = Math.min(
        CONTROL_PANEL_MAX_WIDTH,
        Math.max(CONTROL_PANEL_MIN_WIDTH, proposedWidth)
      )
      setControlPanelWidth(constrainedWidth)
    }

    const handleMouseUp = () => {
      setIsResizingControlPanel(false)
      stopControlPanelResize()
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp, { once: true })

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      stopControlPanelResize()
    }
  }, [isResizingControlPanel, stopControlPanelResize])

  useEffect(() => {
    if (!isStatementsCollapsed) {
      return
    }
    if (isResizingControlPanel) {
      setIsResizingControlPanel(false)
      stopControlPanelResize()
    }
  }, [isStatementsCollapsed, isResizingControlPanel, stopControlPanelResize])

  const handleOutputPanelResizeStart = useCallback(
    (event) => {
      if (isOutputCollapsed) return
      event.preventDefault()
      outputResizeSnapshotRef.current = {
        startX: event.clientX,
        startWidth: outputPanelWidth
      }
      setIsResizingOutputPanel(true)
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'
    },
    [outputPanelWidth, isOutputCollapsed]
  )

  useEffect(() => {
    if (!isResizingOutputPanel) {
      return
    }

    const handleMouseMove = (event) => {
      const delta = outputResizeSnapshotRef.current.startX - event.clientX
      const proposedWidth = outputResizeSnapshotRef.current.startWidth + delta
      const constrainedWidth = Math.min(700, Math.max(300, proposedWidth))
      setOutputPanelWidth(constrainedWidth)
    }

    const handleMouseUp = () => {
      setIsResizingOutputPanel(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp, { once: true })

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizingOutputPanel])

  useEffect(() => {
    if (!isOutputCollapsed) return
    if (isResizingOutputPanel) {
      setIsResizingOutputPanel(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isOutputCollapsed, isResizingOutputPanel])

  useEffect(() => () => stopControlPanelResize(), [stopControlPanelResize])

  useEffect(() => {
    setStreamInstance(selectedInstance ?? '')
  }, [selectedInstance, setStreamInstance])

  useEffect(() => {
    if (!activeStatement?.statementName) {
      setStreamStatement('')
    }
  }, [activeStatement, setStreamStatement])

  const renderControlPanel = () => {
    if (isStatementsCollapsed) {
      return (
        <section
          className="as-panel as-panel-collapsed"
          aria-label="Statements list"
          data-testid="statements-column"
          data-collapsed="true"
        >
          <button
            type="button"
            className="as-collapse-toggle"
            onClick={() => setIsStatementsCollapsed(false)}
            aria-label="Expand statements column"
            aria-expanded="false"
          >
            ▶
          </button>
          <div className="as-collapsed-indicator" title="Statements">
            <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>Statements</span>
          </div>
        </section>
      )
    }

    return (
      <div
        className="as-control-panel-wrapper"
        style={{ width: `${Math.round(controlPanelWidth)}px`, position: 'relative' }}
        data-testid="statements-column"
        data-collapsed="false"
      >
        <section className="as-panel" aria-label="Statements list">
          <header className="statements-control-header">
            <h3>Statements</h3>
            <button
              type="button"
              className="as-collapse-toggle"
              onClick={() => setIsStatementsCollapsed(true)}
              aria-label="Collapse statements column"
              aria-expanded="true"
            >
              ◀
            </button>
          </header>

          <StatementList
            statements={filteredStatements}
            totalStatements={statements.length}
            filters={filters}
            availableInstances={availableInstances}
            onFiltersChange={handleFiltersChange}
            onDelete={handleDeleteStatement}
            onRefresh={loadStatements}
            onSelect={handleStatementSelect}
            selectedStatementId={activeStatement?.id ?? null}
            isLoading={listStatus === 'loading' || listStatus === 'refreshing'}
            isError={listStatus === 'error'}
            error={listError}
            deletingId={deletingId}
          />
        </section>

        <div
          className="as-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize statements column"
          data-testid="list-resizer"
          onMouseDown={handleControlPanelResizeStart}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = 'rgba(88, 166, 255, 0.1)'
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = 'transparent'
          }}
          style={{
            position: 'absolute',
            right: '-5px',
            top: 0,
            bottom: 0,
            width: '10px',
            cursor: 'ew-resize',
            zIndex: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            transition: 'background 0.2s ease'
          }}
        >
          <div
            style={{
              width: '3px',
              height: '40px',
              background: isResizingControlPanel ? 'var(--as-accent)' : 'var(--as-border)',
              borderRadius: '2px',
              opacity: isResizingControlPanel ? 1 : 0.5,
              transition: 'all 0.2s ease'
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="as-app statements-app">
      <main
        className={`as-layout ${isStatementsCollapsed ? 'as-control-panel-collapsed' : ''}`}
        style={layoutStyle}
        data-testid="statement-layout"
      >
        {renderControlPanel()}
        <section className="statements-workspace">
          <div className="statements-editor-pane" data-testid="editor-column">
            <StatementForm
              onCreate={handleCreateStatement}
              onValidate={handleValidateStatement}
              onCancelEdit={handleCancelEdit}
              activeStatement={activeStatement}
              fieldSchema={FIELD_SCHEMA}
              eventSamples={eventSamples}
              onRecordValidationResult={recordValidationResult}
              isSubmitting={isSubmitting}
              isValidating={isValidating}
              validationFeedback={validationFeedback}
            />
          </div>
          {isOutputCollapsed ? (
            <aside
              className="as-panel as-panel-collapsed"
              aria-label="Output"
              data-testid="output-column"
              data-collapsed="true"
            >
              <button
                type="button"
                className="as-collapse-toggle"
                onClick={() => setIsOutputCollapsed(false)}
                aria-label="Expand output panel"
                aria-expanded="false"
              >
                ◀
              </button>
              <div className="as-collapsed-indicator" title="Output">
                <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>Output</span>
              </div>
            </aside>
          ) : (
            <div
              className="statements-output-wrapper"
              style={{ width: `${Math.round(outputPanelWidth)}px`, position: 'relative' }}
              data-testid="output-column"
              data-collapsed="false"
            >
              <div
                className="as-resize-handle"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize output panel"
                onMouseDown={handleOutputPanelResizeStart}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = 'rgba(88, 166, 255, 0.1)'
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = 'transparent'
                }}
                style={{
                  position: 'absolute',
                  left: '-5px',
                  top: 0,
                  bottom: 0,
                  width: '10px',
                  cursor: 'ew-resize',
                  zIndex: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  transition: 'background 0.2s ease'
                }}
              >
                <div
                  style={{
                    width: '3px',
                    height: '40px',
                    background: isResizingOutputPanel ? 'var(--as-accent)' : 'var(--as-border)',
                    borderRadius: '2px',
                    opacity: isResizingOutputPanel ? 1 : 0.5,
                    transition: 'all 0.2s ease'
                  }}
                />
              </div>
              <aside className="statements-output-pane">
                <div className="statements-output-header">
                  <h3>Output</h3>
                  <button
                    type="button"
                    className="as-collapse-toggle"
                    aria-expanded="true"
                    onClick={() => setIsOutputCollapsed(true)}
                    aria-label="Collapse output panel"
                  >
                    ▶
                  </button>
                </div>
                <StatementOutput
                  validationLog={validationLog}
                  events={streamEvents}
                  activeStatement={activeStatement}
                />
              </aside>
            </div>
          )}
        </section>
      </main>
    </div>
  )
}

export default StatementsPanel
